import React, { useState } from "react";
import { Modal, Form, InputNumber, DatePicker, Row, Col, Typography, message } from "antd";
import dayjs from "dayjs";
import { updatePharmacyMedicineBatchApi } from "../../../services/pharmacyMedicines.services";
import { rateNotAboveMrpRules, RATE_FIELDS } from "../common/validators";

/**
 * Edits price/date fields on an existing batch. batch_number and available_qty
 * are shown read-only - stock/lot identity must flow through purchase/dispense/
 * return, not a raw edit here.
 */
const BatchEditModal = ({ open, setOpen, medicineId, batch, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
      const payload = {
        ...values,
        expiry: values.expiry.endOf("month").format("YYYY-MM-DD"),
        manufacturing_date: values.manufacturing_date ? values.manufacturing_date.format("YYYY-MM-DD") : null,
      };
      setLoading(true);
      updatePharmacyMedicineBatchApi(medicineId, batch.id, payload)
        .then(() => {
          message.success("Batch updated successfully");
          setLoading(false);
          setOpen(false);
          onSaved?.();
        })
        .catch((err) => {
          message.error(err?.response?.data?.message || "Failed to update batch");
          setLoading(false);
        });
      })
      .catch(() => {
        // Validation failure - antd rejects with a plain object and has already
        // flagged the fields; swallowed so it does not surface as an unhandled
        // rejection in the error overlay.
      });
  };

  return (
    <Modal
      title="Edit Batch"
      open={open}
      onCancel={() => setOpen(false)}
      onOk={handleOk}
      centered
      width={520}
      okText="Save"
      confirmLoading={loading}
      destroyOnClose
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Typography.Text type="secondary">Batch Number</Typography.Text>
          <div>{batch.batch_number}</div>
        </Col>
        <Col span={12}>
          <Typography.Text type="secondary">Available Qty</Typography.Text>
          <div>{batch.available_qty ?? 0}</div>
        </Col>
      </Row>

      <Form
        layout="vertical"
        form={form}
        initialValues={{
          expiry: batch.expiry ? dayjs(batch.expiry) : null,
          manufacturing_date: batch.manufacturing_date ? dayjs(batch.manufacturing_date) : null,
          mrp: batch.mrp ?? 0,
          purchase_rate: batch.purchase_rate ?? 0,
          selling_price: batch.selling_price ?? 0,
          rate_b: batch.rate_b ?? 0,
          rate_c: batch.rate_c ?? 0,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            {/* The month is what a pack prints; the day it is kept as is that
                month's last - see BatchDetailsModal. */}
            <Form.Item label="Expiry" name="expiry" rules={[{ required: true, message: "Please select expiry" }]}>
              <DatePicker
                picker="month"
                format="MMM YYYY"
                placeholder="Select month"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Manufacturing Date" name="manufacturing_date">
              <DatePicker style={{ width: "100%" }} disabledDate={(d) => d && d > dayjs().endOf("day")} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="MRP" name="mrp" rules={[{ required: true, message: "Required" }]}>
              <InputNumber style={{ width: "100%" }} min={0}
                onChange={() =>
                  form.validateFields(RATE_FIELDS).catch(() => {})
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Purchase Rate" name="purchase_rate">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Selling Price (Rate A)"
              name="selling_price"
              rules={[
              ...rateNotAboveMrpRules(form, "Selling price"),
              { required: true, message: "Required" }]}
            >
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Rate B" name="rate_b"
              rules={rateNotAboveMrpRules(form, "Rate B")}>
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Rate C" name="rate_c"
              rules={rateNotAboveMrpRules(form, "Rate C")}>
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default BatchEditModal;
